import axios from "axios";

/**
 * Generate a unique temporary upload identifier.
 * Format: temp-{uuid}-{username}
 */
export function generateTempId() {
    const passData = JSON.parse(sessionStorage.getItem("passData"));
    const username = passData?.user || "unknown";
    const uuid = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : Math.random().toString(36).substring(2) + Date.now().toString(36);
    const tempId = `temp-${uuid}-${username}`;
    return tempId;
}

/**
 * Upload a file to a temporary S3 location using tempId instead of projectId.
 * Follows the existing SPE-first, CDX-fallback pattern.
 */
export async function uploadTempFile(file, tempId, fileName, onSuccess, onFail) {
    const options = { isTempUpload: true, tempId };
    const uploadStatus = await SPEFileUpload(
        file, "", null, null, null, onSuccess, options
    );
    if (uploadStatus === 'completed') {
        return uploadStatus;
    } else {
        return uploadFileToCDX(
            file, "", null, null, null, fileName, onSuccess, onFail, options
        );
    }
}

export async function uploadFile(file,
    note,
    persId,
    phaseId,
    projectId,
    fileName,
    onSuccess,
    onFail,
    options = {}) {
    const uploadStatus = await SPEFileUpload(file,
        note,
        persId,
        phaseId,
        projectId,
        onSuccess,
        options); // onFail withheld — CDX fallback has not been tried yet
    if (uploadStatus === 'completed') {
        return uploadStatus;
    } else {
        console.log("Upload via CDX")
        return uploadFileToCDX(file,
            note,
            persId,
            phaseId,
            projectId,
            fileName,
            onSuccess,
            onFail,
            options);
    }
}

export async function uploadFileToCDX(file, 
    note,
    persId,
    phaseId,
    projectId,
    fileName,
    onSuccess,
    onFail,
    options = {}) {
    let status = '';
    const uploadUrlRequest = {
        appCode: window.location.hostname == "eitcg.cdxms.devl-etss.aws.fanniemae.com" ? "eitcg-ess" : "eitcg",
        fileNames: [
            {
                filename: fileName,
                fileId:  Math.floor(Math.random() * 1000),
            },
        ],
    };
    let fileUploadId;
    await axios
    .post(
      `/cdx-filupldproc/fileupload/processing/upload/url`,
      uploadUrlRequest
    )
    .then(async (response) => {
      if (response.status == 200 || response.status == 201) {
        if (response.data[0].url) {
          fileUploadId = response.data[0].uploadReferenceId;
          await axios
            .put(response.data[0].url, file, {
              headers: {
                "content-type": file.type,
                Accept: "*/*",
                "Access-Control-Allow-Origin": "*",
              },
            })
            .then(async (response) => {
              if (response.status == 200 || response.status == 201) {
                console.log('uploadFileToCDX', response.data)
                status =  await uploadStatusFromApigee(
                  fileUploadId,
                  note,
                  persId,
                  phaseId,
                  projectId,
                  fileName,
                  new Date(),
                  onSuccess,
                  onFail,
                  options
                );
              } else if (response.data && response.data.message) {
                status =  "error";
              }
            })
            .catch((err) => {
              console.log("Error ", err)
              status =  "error";
              onFail?.({err, note, persId, phaseId, projectId, fileName})
            });
        } else if (response.data && response.data.message) {
             status =  "error";
        }
      }
    })
    .catch((err) => {
      console.log("Error ", err)
      status =  "error";
      onFail?.({err, note, persId, phaseId, projectId, fileName})
    });

    return status;
}

export const uploadStatusFromApigee = async (
    uploadReferenceId,
    note,
    persId,
    phaseId,
    projectId,
    fileName,
    startTime,
    onSuccess,
    onFail,
    options = {}
  ) => {
    let apiTimeout;
    let status = '';
    if (uploadReferenceId) {
      await axios
        .get(
          `/cdx-filupldproc/fileupload/processing/status/` + uploadReferenceId
        )
        .then(async (res) => {
          if (res.status == 200) {
            if (
              res.data.status == "REQUESTED" ||
              res.data.status == "SCANNED" ||
              res.data.status == "VIRUS_SCAN_STARTED"
            ) {
              apiTimeout = setTimeout(
                uploadStatusFromApigee(
                  uploadReferenceId,
                  note,
                  persId,
                  phaseId,
                  projectId,
                  fileName,
                  startTime,
                  onSuccess,
                  onFail,
                  options
                ),
                10000
              );
            } else if (
              res.data.status == "COMPLETED" ||
              res.data.status == "SCANNED"
            ) {
              clearTimeout(apiTimeout);
              let fileUploadContext = {
                appCode: res.data.appCode,
                fileName: res.data.fileName,
                fileUploadId: res.data.fileUploadId,
                fileUploadNotes: note,
                letterId: 0,
                message: res.message,
                persId: persId,
                phaseId: phaseId,
                projectId: (options.isTempUpload && options.tempId) ? null : projectId,
                receivedId: res.data.userId,
                s3Bucket: res.data.s3Bucket,
                status: res.data.status,
                userId: res.data.userId,
              };
              if (options.isTempUpload && options.tempId) {
                fileUploadContext.isTempUpload = true;
                fileUploadContext.tempId = options.tempId;
              }
              status = await uploadFileToSystem(fileUploadContext, fileName, 0, startTime, onSuccess, onFail);
            } else if (res.data.status == "ERROR") {
              clearTimeout(apiTimeout);
              status = "error";
            }
          } else {
            clearTimeout(apiTimeout);
            status = "error";
          }
        })
        .catch((err) => {
            console.log("Error ", err)
            clearTimeout(apiTimeout);
            status = "error";
        });
    } else {
      status = "error";
    }
    return status;
  };

  export const uploadFileToSystem = async (uploadContext, fileName, count, startTime, onSuccess, onFail) => {
     let retry = count; 
     let status = ''
      await axios
        .post(
          global.REACT_APP_API_ENDPOINT + `/docStorage/postFileuploadContext`,
          uploadContext
        )
        .then(response => {
          if (response.status == 200) {
            status = "completed";
            console.log('uploadFileToSystem', response, onSuccess)
            onSuccess?.(response.data)
          }
          // onFail?
        })
        .catch((err) => {
          console.log("Error ", err)
          if (retry <= 10) {
            retry += 1
            const mSecds = 10000 * retry;
            setTimeout(function () {
              uploadFileToSystem(uploadContext, fileName, retry, startTime, onSuccess, onFail);
            }, mSecds);
          } else {
             status = "error";
             onFail?.({err, uploadContext, fileName, startTime});
          }
        });

        return status;
    };


export async function SPEFileUpload(file, 
    note,
    persId,
    phaseId,
    projectId,
    onSuccess,
    options = {}) {
        const CHUNK_SIZE = 40 * 1024 * 1024; 
        let uploadStatus = "";
        let totalChunks = Math.ceil(file.size / CHUNK_SIZE);
        let uploadId = '';
        let streamId = '';
        let failed = false;
        const passData = JSON.parse(sessionStorage.getItem("passData"));
        const user = passData?.user;
        const retryLimit = 5;        
        for ( let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
          if(failed) {
            break;
          }
          let chunk = file.slice(chunkIndex * CHUNK_SIZE, (chunkIndex+1) * CHUNK_SIZE);
          let leftOverChunkSize = (file.size - (chunkIndex+1)*CHUNK_SIZE);
          if (totalChunks > 1 && (leftOverChunkSize > 0 && leftOverChunkSize < 5 *1024 *1024)) {
            chunk = file.slice(chunkIndex*CHUNK_SIZE, file.size);
            totalChunks--;
          }
          const formData = new FormData();
          formData.append("fileChunk", chunk);
          formData.append("chunkIndex",chunkIndex);
          formData.append("totalChunks", totalChunks);
          formData.append('fileName',file.name);
          formData.append('uploadId', uploadId);
          formData.append('streamId', streamId);
          formData.append("user", user);
          formData.append("content-type", file.type);
          if (options.isTempUpload && options.tempId) {
            formData.append("isTempUpload", "true");
            formData.append("tempId", options.tempId);
          } else {
            formData.append("projectId", projectId);
          }
          if(phaseId) {
            formData.append("phaseId", phaseId);
          }
          if(persId) {
            formData.append("persId", persId);
          }
          formData.append("note", note);
          formData.append("letterId", 0);
          formData.append("fileSize", file.size);
          let retry = true
          let retryCount = 0;
          
          // Implementing retry mechanism for each chunk upload with a limit of 5 retries per chunk
          while (retry && retryCount <= retryLimit) {
            if (retryCount > 0) {
              console.log(`Retrying upload for chunk ${chunkIndex+1}, attempt ${retryCount + 1 }`);
            }

            retryCount++;

            await axios
              .post(
                global.REACT_APP_API_ENDPOINT + `/docStorage/scanandupload`,
                formData
              )
              .then((response) => {
                if (!response || !response.data || !response.data.status) {
                  failed = true;
                  uploadStatus = "error";
                } else {
                  if (response.data.status === 'INIT') {
                    streamId = response.data.streamId;
                    uploadId = response.data.uploadId;
                  }
                  console.log(`Successfully uploaded chunk ${chunkIndex + 1}`);
                  retry = false;
                  failed = false;
                  uploadStatus = "";
                  if (response.data.status === 'SCAN_COMPLETE') {
                    uploadStatus = "completed";
                    console.log('SPEFileUpload', response, onSuccess)
                    onSuccess?.(response.data.uploadDetails)
                  }
                }
              }).catch(error => {
                console.log("Error ", error)
                failed = true;
                uploadStatus = "error";
              });
          }

          // Do not invoke onFail, but show a console warning to show we are going to fallback to CDX 
          if (failed) {
            console.warn(`Chunk ${chunkIndex + 1} failed after ${retryCount} attempts. Falling back to CDX upload.`);
          }
        }
        return uploadStatus;
}

